import { useEffect, useRef, useState } from 'react'
import { CheckCircleIcon } from '@heroicons/react/24/solid'

type Status = 'pending' | 'in_progress' | 'done'
type Variant = 'generate-ads' | 'scrape-products'

/** When each step hands over to the next; the last stays in progress until the work finishes. */
const THRESHOLDS: Record<Variant, number[]> = {
  'generate-ads': [5_000, 15_000, 21_000, 27_000, 33_000],
  'scrape-products': [5_000, 12_000],
}
const FLASH_STEP_DELAY = 180
const HIDE_AFTER_COMPLETE_DELAY = 350

const HEADERS: Record<Variant, string> = {
  'generate-ads': 'Generating your ads with AI',
  'scrape-products': 'Updating your products',
}

const STEPS: Record<Variant, string[]> = {
  'generate-ads': [
    'Analyzing your brand website...',
    "Analyzing brand's ads and taglines...",
    'Understanding your brand style and voice...',
    'Crafting ad concepts...',
    'Writing ad copy and headlines...',
    'Finalizing your ads...',
  ],
  'scrape-products': ['Fetching product catalog...', 'Extracting details...', 'Finalizing...'],
}

function PreloaderSpinner() {
  return (
    <div className="preloader mx-auto mb-4" aria-hidden>
      {[0, 1, 2, 3, 4, 5].map((slice) => (
        <span key={slice} />
      ))}
    </div>
  )
}

type Props = {
  variant: Variant
  isActive: boolean
  onHidden?: () => void
}

export default function AdFieldsLoader({ variant, isActive, onHidden }: Props) {
  const labels = STEPS[variant]
  const [statuses, setStatuses] = useState<Status[]>(() =>
    labels.map((_, index) => (index === 0 ? 'in_progress' : 'pending')),
  )
  const [visible, setVisible] = useState(true)
  const statusesRef = useRef(statuses)

  useEffect(() => {
    statusesRef.current = statuses
  }, [statuses])

  useEffect(() => {
    if (!isActive) return

    const timers = THRESHOLDS[variant].map((delay, index) =>
      setTimeout(() => {
        setStatuses((current) =>
          current.map((status, position) => {
            if (position === index) return 'done'
            if (position === index + 1) return 'in_progress'
            return status
          }),
        )
      }, delay),
    )

    return () => timers.forEach(clearTimeout)
  }, [isActive, variant])

  useEffect(() => {
    if (isActive) return

    const firstIncomplete = statusesRef.current.findIndex((status) => status !== 'done')
    const timers: ReturnType<typeof setTimeout>[] = []
    let delay = 0

    if (firstIncomplete !== -1) {
      for (let index = firstIncomplete; index < statusesRef.current.length; index += 1) {
        const position = index
        timers.push(
          setTimeout(() => {
            setStatuses((current) => current.map((status, at) => (at === position ? 'done' : status)))
          }, delay),
        )
        delay += FLASH_STEP_DELAY
      }
    }

    timers.push(
      setTimeout(() => {
        setVisible(false)
        onHidden?.()
      }, delay + HIDE_AFTER_COMPLETE_DELAY),
    )

    return () => timers.forEach(clearTimeout)
  }, [isActive, onHidden])

  return (
    <div
      className={`absolute inset-0 z-10 flex flex-col items-center justify-center rounded-xl bg-white/95 px-6 backdrop-blur-sm transition-opacity ${
        visible ? 'opacity-100 duration-150' : 'opacity-0 duration-250'
      }`}
    >
      <div className="mb-6 text-center">
        <PreloaderSpinner />
        <span className="text-2xl font-semibold text-gray-800">{HEADERS[variant]}</span>
      </div>

      <div className="max-w-lg space-y-4">
        {labels.map((label, index) => {
          const status = statuses[index] ?? 'pending'
          return (
            <div key={label} className="flex items-center gap-3">
              <span className="flex size-6 shrink-0 items-center justify-center">
                {status === 'done' ? (
                  <CheckCircleIcon className="size-6 text-green-500" />
                ) : status === 'in_progress' ? (
                  <span className="size-6 animate-spin rounded-full border-2 border-indigo-200 border-t-indigo-500" />
                ) : (
                  <span className="size-5 rounded-full border-2 border-gray-200" />
                )}
              </span>
              <span
                className={`text-base leading-snug ${
                  status === 'done'
                    ? 'font-medium text-green-700'
                    : status === 'in_progress'
                      ? 'font-medium text-indigo-700'
                      : 'text-gray-300'
                }`}
              >
                {label}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}
